import { Link } from 'react-router-dom';

export interface ConsentValues {
  consentSms:     boolean;
  consentTerms:   boolean;
  marketingOptIn: boolean;
}

interface ConsentCheckboxesProps {
  values:   ConsentValues;
  onChange: (values: ConsentValues) => void;
  disabled?: boolean;
}

/**
 * The consent checkboxes shown at signup and OAuth consent collection.
 *
 * Checkbox 1 — OPTIONAL: SMS reminders. Must NOT be required. A2P 10DLC
 *   carriers reject campaigns where SMS consent is a required condition of
 *   service (error 30923), so this box is opt-in only and the service works
 *   fully without it (notifications fall back to email). The copy includes
 *   "Consent is not a condition of purchase" per CTIA guidance.
 * Checkbox 2 — REQUIRED: Privacy Policy + Terms of Service
 * Checkbox 3 — OPTIONAL: Partner marketing emails
 *
 * These must be shown at account creation and at OAuth consent collection.
 * The timestamps (consent_sms_ts, consent_terms_ts) are written server-side
 * so they can't be spoofed by the client.
 */
export default function ConsentCheckboxes({
  values,
  onChange,
  disabled = false,
}: ConsentCheckboxesProps) {
  const toggle = (field: keyof ConsentValues) => {
    onChange({ ...values, [field]: !values[field] });
  };

  const checkboxClass = `
    w-4 h-4 rounded border-gray-300 text-sage
    focus:ring-2 focus:ring-sage focus:ring-offset-1
    cursor-pointer transition-colors duration-150
    disabled:cursor-not-allowed disabled:opacity-50
  `.trim();

  return (
    <div className="space-y-3">

      {/* ── Checkbox 1: SMS consent — OPTIONAL (A2P 30923 compliance) ───────── */}
      <label className={`flex items-start gap-3 cursor-pointer group ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input
          type="checkbox"
          checked={values.consentSms}
          onChange={() => toggle('consentSms')}
          disabled={disabled}
          className={checkboxClass}
          style={{ marginTop: 2, accentColor: '#E8C12E', flexShrink: 0 }}
        />
        <span className="font-body text-xs text-gray-500 leading-relaxed">
          <span className="font-medium text-gray-600">Optional: </span>
          I agree to receive SMS reminders about my scheduled card mailings from MomentSent.
          Consent is not a condition of purchase. Msg &amp; data rates may apply. Message
          frequency varies.{' '}
          <span className="text-gray-400">Reply STOP to opt out, HELP for help.</span>
        </span>
      </label>

      {/* ── Checkbox 2: Terms + Privacy — REQUIRED ─────────────────────────── */}
      <label className={`flex items-start gap-3 cursor-pointer group ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input
          type="checkbox"
          checked={values.consentTerms}
          onChange={() => toggle('consentTerms')}
          disabled={disabled}
          className={checkboxClass}
          style={{ marginTop: 2, accentColor: '#E8C12E', flexShrink: 0 }}
          aria-required="true"
        />
        <span className="font-body text-xs text-gray-600 leading-relaxed">
          <span className="font-semibold text-navy">Required: </span>
          I agree to the{' '}
          <Link
            to="/privacy"
            target="_blank"
            className="text-sage underline underline-offset-2 hover:text-navy transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            Privacy Policy
          </Link>
          {' '}and{' '}
          <Link
            to="/terms"
            target="_blank"
            className="text-sage underline underline-offset-2 hover:text-navy transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            Terms of Service
          </Link>
          .
        </span>
      </label>

      {/* ── Checkbox 3: Marketing opt-in — OPTIONAL ────────────────────────── */}
      <label className={`flex items-start gap-3 cursor-pointer group ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <input
          type="checkbox"
          checked={values.marketingOptIn}
          onChange={() => toggle('marketingOptIn')}
          disabled={disabled}
          className={checkboxClass}
          style={{ marginTop: 2, accentColor: '#E8C12E', flexShrink: 0 }}
        />
        <span className="font-body text-xs text-gray-500 leading-relaxed">
          <span className="font-medium text-gray-600">Optional: </span>
          I'd like to receive exclusive offers and updates from MomentSent via email.
        </span>
      </label>

    </div>
  );
}
